import 'arrive';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Counter helpers (per animare i numeri al pin della sezione)
function formatNumber(value, decimals, sep) {
    const n = Number(value);
    if (!isFinite(n)) return String(value);
    const fixed = n.toFixed(decimals);
    const [int, dec] = fixed.split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return dec ? `${withSep},${dec}` : withSep;
}

function parseNumberFromText(el) {
    const raw = (el.textContent || '').trim();
    if (!raw) return 0;
    const cleaned = raw.replace(/[^0-9.,-]/g, '');
    if (cleaned.includes(',') && cleaned.includes('.')) {
        return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
    }
    return parseFloat(cleaned.replace(',', '.')) || 0;
}

// FASE 1 — costruisce le colonne rolling con strip a y:0 (mostra "0"), nessuna animazione
function buildRollingColumns(el) {
    if (el.dataset.counterReady === 'true') return;
    el.dataset.counterReady = 'true';

    const end = parseFloat(el.getAttribute('data-target')) || parseNumberFromText(el) || 0;
    const decimals = Math.max(0, parseInt(el.getAttribute('data-decimals') || '0', 10));
    const sep = el.getAttribute('data-sep') || '.';

    const probe = document.createElement('span');
    probe.style.cssText = 'visibility:hidden;position:absolute;white-space:nowrap;';
    probe.textContent = '0';
    el.appendChild(probe);
    const h = probe.getBoundingClientRect().height || parseFloat(getComputedStyle(el).lineHeight) || 40;
    el.removeChild(probe);

    el.dataset.counterH = h;
    const formatted = formatNumber(end, decimals, sep);
    el.innerHTML = '';
    el.style.display = 'inline-flex';
    el.style.alignItems = 'flex-start';

    [...formatted].forEach((char) => {
        if (!/\d/.test(char)) {
            const s = document.createElement('span');
            s.textContent = char;
            s.style.display = 'inline-block';
            el.appendChild(s);
            return;
        }

        const digit = parseInt(char, 10);

        const col = document.createElement('span');
        col.style.cssText = `display:inline-block;overflow:hidden;height:${h}px;`;

        const strip = document.createElement('span');
        strip.style.cssText = 'display:block;';
        strip.dataset.targetDigit = digit;

        for (let d = 0; d <= 9; d++) {
            const span = document.createElement('span');
            span.textContent = String(d);
            span.style.cssText = `display:block;height:${h}px;line-height:${h}px;`;
            strip.appendChild(span);
        }

        col.appendChild(strip);
        el.appendChild(col);
    });
}

function initNumberColumns(section) {
    // Nasconde i numeri finché la sezione non viene pinnata.
    // Se il counter ha già animato (es. re-init via arrive) non re-nascondere.
    section.querySelectorAll('.number-wrapper').forEach(nw => {
        const counter = nw.querySelector('.number');
        if (counter && counter.dataset.counterPlayed === 'true') return;
        nw.style.visibility = 'hidden';
    });
}

// FASE 2 — al pin, costruisce le colonne e anima verso la cifra target
function playNumberAnimation(section) {
    section.querySelectorAll('.number-wrapper .number').forEach(el => {
        if (el.dataset.counterPlayed === 'true') return;
        el.dataset.counterPlayed = 'true';

        // Costruisce le colonne rolling (a y:0 → mostra "0")
        buildRollingColumns(el);

        // Rende visibile il wrapper ora che le colonne sono pronte
        const wrapper = el.closest('.number-wrapper');
        if (wrapper) wrapper.style.visibility = 'visible';

        // Anima i strip verso la cifra target
        const h = parseFloat(el.dataset.counterH) || 40;
        el.querySelectorAll('[data-target-digit]').forEach((strip, i) => {
            const digit = parseInt(strip.dataset.targetDigit, 10);
            gsap.to(strip, { y: -(digit * h), duration: 2.5, ease: 'power2.out', delay: i * 0.06 });
        });
    });
}

// Utility: Trova i gruppi di sezioni full-height adiacenti
function getAdjacentClusters(nodes) {
    const clusters = [];
    let current = [];
    nodes.forEach((el, idx) => {
        const prev = nodes[idx - 1];
        if (!prev) {
            current = [el];
            return;
        }
        const isAdjacent = prev.nextElementSibling === el;
        if (isAdjacent) {
            current.push(el);
        } else {
            if (current.length >= 2) clusters.push(current);
            current = [el];
        }
    });
    if (current.length >= 2) clusters.push(current);
    return clusters;
}

function setupMediaTextStack() {
    const allFull = Array.from(document.querySelectorAll('.block-media-text.block-media-text__full'));
    if (!allFull.length) return;

    // 1. RECUPERO IL PADDING REALE DAL TUO CONTENITORE
    // Sostituisci '.main' o '#main' con il selettore esatto che usi nel tuo progetto
    const mainContainer = document.querySelector('.main') || document.querySelector('#main') || document.body;
    
    const getPaddingValue = () => {
        const style = window.getComputedStyle(mainContainer);
        const pad = parseFloat(style.paddingTop);
        // console.log('Padding dinamico rilevato:', pad); // Se vedi un numero > 0, ci siamo!
        return pad || 0;
    };

    // Pulizia dei trigger esistenti
    ScrollTrigger.getAll().forEach((st) => {
        if (st.vars && st.vars.id && String(st.vars.id).startsWith('media-text-stack-')) {
            st.kill();
        }
    });

    const clusters = getAdjacentClusters(allFull);
    
    // Sezioni full-height che non appartengono a nessun cluster (isolate).
    // Non vengono pinnate, ma il loro counter va comunque animato al primo ingresso.
    const clusteredEls = new Set(clusters.flat());
    allFull.forEach((el, i) => {
        if (clusteredEls.has(el)) return;
        initNumberColumns(el);
        const stOrphan = ScrollTrigger.create({
            id: `media-text-stack-orphan-${i}`,
            trigger: el,
            start: () => `top bottom-=10%`,
            once: true,
            onEnter: () => playNumberAnimation(el),
        });
        if (stOrphan.isActive || stOrphan.progress > 0) playNumberAnimation(el);
    });

    clusters.forEach((cluster, cIdx) => {
        cluster.forEach((el, i) => {
            const isLast = i === cluster.length - 1;
            
            // Forza lo z-index via JS per lo stacking corretto
            el.style.zIndex = i + 10;

            if (isLast) {
                el.classList.remove('is-pinned');
                // Anima i counter dell'ultima sezione al suo ingresso in viewport (senza pin)
                initNumberColumns(el);
                const stLast = ScrollTrigger.create({
                    id: `media-text-stack-${cIdx}-${i}-last`,
                    trigger: el,
                    start: () => `top top+=${getPaddingValue()}`,
                    once: true,
                    onEnter: () => playNumberAnimation(el),
                });
                // Se già in range al momento della creazione
                if (stLast.isActive || stLast.progress > 0) playNumberAnimation(el);
                return;
            }

            // Prepara subito le colonne rolling a 0 (prima del pin)
            initNumberColumns(el);

            const st = ScrollTrigger.create({
                id: `media-text-stack-${cIdx}-${i}`,
                trigger: el,
                // Adesso 'start' userà il valore dinamico del clamp prelevato dal tuo .main
                start: () => `top top+=${getPaddingValue()}`,
                endTrigger: cluster[cluster.length - 1],
                end: () => `top top+=${getPaddingValue()}`,
                pin: true,
                pinSpacing: false,
                anticipatePin: 1,
                invalidateOnRefresh: true, // Fondamentale per ricalcolare il clamp se ridimensioni la finestra
                markers: false, // Controlla ora: i markers dovrebbero essere scesi!
                onToggle: self => {
                    el.classList.toggle('is-pinned', self.isActive);
                    if (self.isActive) playNumberAnimation(el);
                }
            });
            // Se il trigger viene creato mentre siamo già in range (refresh/re-init),
            // onToggle non scatta: animiamo subito
            if (st.isActive) {
                el.classList.add('is-pinned');
                playNumberAnimation(el);
            }
        });
    });
}

function init() {
    // Configurazione MatchMedia: Solo da LG (992px) in su
    let mm = gsap.matchMedia();
    
    mm.add('(min-width: 992px)', () => {
        setupMediaTextStack();
        return () => {
            // Cleanup totale quando scendiamo sotto i 992px
            ScrollTrigger.getAll().forEach((st) => {
                if (st.vars && st.vars.id && String(st.vars.id).startsWith('media-text-stack-')) {
                    st.kill();
                }
            });
            // Reset stili inline
            document.querySelectorAll('.block-media-text--stack').forEach(el => {
                el.style.zIndex = '';
                el.style.marginTop = '';
            });
        };
    });
}

// Avvio
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Gestione blocchi dinamici con debounce
let mtStackRaf = 0;
document.arrive('.block-media-text', { existing: true }, function () {
    if (this.classList.contains('block-media-text__full') && window.innerWidth >= 992) {
        if (mtStackRaf) cancelAnimationFrame(mtStackRaf);
        mtStackRaf = requestAnimationFrame(() => {
            setupMediaTextStack();
            ScrollTrigger.refresh();
        });
    }
});